// src/utils/reverseSearchFormat.js
/**
 * How a reverse-search match is DESCRIBED. Pure functions, no API client.
 *
 * Split out of `ReverseSearchService` for two reasons. The obvious one is
 * testability — importing the service drags in the axios client, Firebase auth
 * and the workspace header, none of which a formatting test wants. The better
 * one is that these definitions are shared by the "Similar" tab and (from Phase
 * 2) the reverse-search results view, and a band that means one thing in one
 * place and something else in another is worse than no band at all.
 *
 * The governing rule (REVERSE_SEARCH_PLAN.md Decision H): **never show a
 * similarity number.** A percentage invites the user to compare two figures
 * they have no way to calibrate. A band plus the evidence that earned it —
 * "Near-identical", "Region at 34%, 12%" — is a claim they can check by
 * looking, which is the only kind of confidence that survives contact with a
 * wrong result.
 */

/** Human label and Chip tone for each match band. */
export const BAND_LABELS = {
    'exact':          { label: 'Identical file',  variant: 'success' },
    'near-identical': { label: 'Near-identical',  variant: 'success' },
    'contains':       { label: 'Part of this',    variant: 'accent' },
    'contained-in':   { label: 'Contains yours',  variant: 'accent' },
    'similar':        { label: 'Similar',         variant: 'accent-subtle' },
    'related':        { label: 'Related',         variant: 'neutral' },
};

const pct = (v) => `${Math.round(v * 100)}%`;

function formatSeconds(total) {
    const seconds = Math.max(0, Math.floor(total));
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/**
 * One evidence item as a short, checkable phrase, or null when it isn't worth
 * a chip.
 *
 * Note the wording difference for regions. A region the server MEASURED (it
 * carries an NCC from stage-2 verification) reads "Region at …"; one it merely
 * inferred from which fragment matched reads "Around …". Same rectangle,
 * different claim, and the phrasing has to carry that or the hedge is invisible.
 */
export function describeEvidence(evidence) {
    if (!evidence) return null;
    switch (evidence.kind) {
        case 'hash':
            return 'Same file contents';
        case 'phash':
            return Number.isFinite(evidence.hamming)
                ? `Visually ${evidence.hamming <= 6 ? 'identical' : 'close'}`
                : null;
        case 'region': {
            const { rect, ncc } = evidence;
            if (!rect) return null;
            const where = `${pct(rect.x)}, ${pct(rect.y)} · ${pct(rect.w)} wide`;
            return ncc == null ? `Around ${where}` : `Region at ${where}`;
        }
        case 'time':
            return `${formatSeconds(evidence.start)} – ${formatSeconds(evidence.end)}`;
        case 'view':
            return `View ${evidence.viewIndex + 1}`;
        case 'coverage':
            // "1 of 9 regions" is noise: one fragment matching is the minimum
            // that can produce a hit at all, so it distinguishes nothing.
            return evidence.matched > 1 ? `${evidence.matched} of ${evidence.total} regions` : null;
        default:
            return null;
    }
}

/**
 * The region rectangle to draw over a candidate's thumbnail, or null.
 *
 * `verified` decides solid vs dashed in the CSS. A guess and a measurement must
 * not look alike — the entire value of drawing the region is that the user can
 * glance at it and judge whether we are right.
 */
export function regionOverlay(hit) {
    const region = (hit?.evidence || []).find((e) => e.kind === 'region');
    if (!region?.rect) return null;
    return { rect: region.rect, verified: region.ncc != null };
}

/**
 * The matched time span to highlight on a result, or null.
 *
 * The audio counterpart of `regionOverlay`, and it carries the same
 * measured-versus-inferred distinction. A span from stage-2 verification has
 * `votes` — hundreds of spectral landmarks independently agreeing on one
 * alignment — and draws solid. A span from stage 1 is only "the window whose
 * descriptor matched", which is a five-second bucket rather than a location,
 * and draws dashed.
 *
 * Fractions are relative to the CANDIDATE's duration, so the strip can be drawn
 * without knowing anything about the track but its length. Returns null when
 * the duration is unknown — a bar with no scale would imply a precision that
 * isn't there.
 */
export function timeSpanOverlay(hit) {
    const span = (hit?.evidence || []).find((e) => e.kind === 'time');
    if (!span) return null;

    const duration = hit?.file?.duration;
    if (!Number.isFinite(duration) || duration <= 0) return null;

    const start = Math.max(0, Math.min(1, span.start / duration));
    const end = Math.max(start, Math.min(1, span.end / duration));

    // A span covering the whole track says nothing — it is what a match on the
    // asset's SUMMARY fragment looks like, and drawing a full-width bar over
    // every such result is noise dressed as information. Exactly the rule
    // `evidenceFor` applies to a full-frame rectangle on the image side.
    if (end - start >= 0.95) return null;

    return {
        start,
        // A sub-second match on a ten-minute track is a sliver too thin to see.
        // Floor the drawn width so the highlight is findable; the exact seconds
        // are in the evidence chip beside it.
        width: Math.max(0.015, end - start),
        verified: span.votes > 0,
        startSec: span.start,
        endSec: span.end,
    };
}

/**
 * A one-line explanation of an index state, or null when nothing needs saying.
 *
 * `scanned` and `truncated` are not errors — the results are exact — but they
 * mean the deployment is missing a Firestore vector index. Someone looking at a
 * slow or short result set deserves to know that, rather than concluding the
 * feature is bad. (DEPLOYMENT.md §5.1.1 is the fix.)
 */
export function describeIndexState(indexState) {
    switch (indexState) {
        case 'scanned':
            return 'Searched without a vector index — results are exact but slower than they should be.';
        case 'truncated':
            return 'Searched only part of the library (no vector index). Some matches may be missing.';
        default:
            return null;
    }
}
